import { useSetupNeed } from "@/hooks/use-setup-need";
import { SETUP_HINT, type SetupNeed } from "@shared/connectors/setupNeed";
import { SkyBadge, SkyProgressBar, SkyText } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import type { DataflowLastRun, DataflowSummary } from "@shared/types/dataflows";
import {
  ConnectionStatusBadge,
  connectionStatusOf,
  connectionStatusPresentation,
  connectionWaitingOf,
  isListedRunning,
  listedRunOwnStatus,
  SettledImportLine,
  settledImportOf,
  waitingLine,
} from "@/components/connections/ConnectionStatus";
import { LiveLine, ViewLink, badgeOf } from "@/components/runs/ImportProgress";
import { StatusCell } from "@/components/runs/StatusCell";
import { runStatusPresentation } from "@/components/runs/runFormatting";
import { newestRunId, useImportAnnouncement, useRunActivity } from "@/hooks/use-run-activity";
import { startedRuns, useStartedRuns, useStartedRunsVersion } from "@/lib/live/InstanceRunFeed";
import { absolutePath } from "@/lib/routes";
import { instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useNow } from "@/hooks/use-now";
import {
  activityLine,
  activityProgress,
  isActivityLive,
  endedWhen,
  type RunActivity,
} from "@/lib/runs/runActivity";

/**
 * A connection's setup state (the save bar's badge), its list cells (Status and Last import),
 * and the outcome of the last thing it did (FR-42, UX-4).
 *
 * Since the owner's review of 2026-09-28 the list and the editor header show the connection's one
 * status (`ConnectionStatus.tsx`: Setup incomplete, Ready, Running, Failed, Active), not this
 * setup state; the save bar keeps the setup state, because Save and Validate are what change it.
 *
 * skypoint-studio's `DataflowStatusBadge.tsx` was not ported: its inputs are
 * Studio's Airflow task counters (`airflowConfig.lastTaskIndex`,
 * `totalTasks`, a websocket pipeline status), none of which exist in this
 * product's model — here `state` is a column the server derives, and per-step
 * progress belongs to the run screens. What survives is its rule, and it is the
 * one that matters: **the colour is never the only signal.** Every badge below
 * carries its word, so it still reads on a monochrome display and to anyone who
 * does not distinguish the hues.
 */

interface SetupPresentation {
  label: string;
  /** Neutral in every state: the colour that means something is the header's status. */
  color: "subtle";
  appearance: "tint" | "outline";
  hint: string;
}

/**
 * The save bar's word for the setup (review r1 S1, 2026-09-28). It used to say "Ready" in green
 * while the header said "Failed" in red on the same screen. The header owns the status and its
 * colour; the save bar names only what Save and Validate change, so it never repeats a status
 * word ("Ready") and never takes a status colour. A draft is drawn exactly as the header draws
 * "Setup incomplete" (hollow, neutral).
 */
const SETUP: Record<DataflowSummary["state"], SetupPresentation> = {
  draft: {
    // "Draft" is ours; "Setup incomplete" is what the reader needs to do (owner, 2026-09-28).
    label: "Setup incomplete",
    color: "subtle",
    appearance: "outline",
    hint: SETUP_HINT.table,
  },
  ready: {
    label: "Setup complete",
    color: "subtle",
    appearance: "tint",
    hint: "Validated, with entities selected. This connection can be scheduled and run.",
  },
  disabled: {
    label: "Disabled",
    color: "subtle",
    appearance: "tint",
    hint: "Turned off by hand. It will not run, even on a schedule.",
  },
};

export function connectionSetupPresentation(
  state: DataflowSummary["state"],
  need: SetupNeed = "table",
): SetupPresentation {
  const setup = SETUP[state];
  return state === "draft" ? { ...setup, hint: SETUP_HINT[need] } : setup;
}

export function ConnectionStateBadge({
  state,
  size = "medium",
  testId = "connection-state-badge",
  need = "table",
}: {
  state: DataflowSummary["state"];
  size?: "medium" | "large";
  testId?: string;
  need?: SetupNeed;
}) {
  const setup = connectionSetupPresentation(state, need);
  return (
    <SkyBadge
      appearance={setup.appearance}
      color={setup.color}
      size={size}
      testId={testId}
      aria-label={`${setup.label}. ${setup.hint}`}
    >
      {setup.label}
    </SkyBadge>
  );
}

export type LastRunOutcome = "running" | "failed" | "succeeded" | "never";

/**
 * What the connection last did, derived from the summary's flags alone: the
 * fallback for a row without `lastRun`. It states exactly what the row knows and
 * says "Not imported yet" rather than inventing a result it cannot see.
 */
export function lastRunOutcomeOf(dataflow: DataflowSummary): LastRunOutcome {
  // #2094 review: the run rows decide over the lock flag, as the Status cell does.
  if (isListedRunning(dataflow)) return "running";
  if (dataflow.isFailed) return "failed";
  if (dataflow.lastRefreshAt || dataflow.lastRunId) return "succeeded";
  return "never";
}

const OUTCOME_LABEL: Record<LastRunOutcome, string> = {
  running: "Running",
  failed: "Failed",
  succeeded: "Succeeded",
  never: "Not imported yet",
};

const OUTCOME_COLOR: Record<LastRunOutcome, "success" | "danger" | "informative" | "subtle"> = {
  running: "informative",
  failed: "danger",
  succeeded: "success",
  never: "subtle",
};

/**
 * The row's import as the list shows it live (Session 10, P12): the newest of this tab's start and
 * the list's `lastRun`, followed while it is queued or running, from the list's own projection
 * plus the tab's feed; nothing is fetched per row. An import this tab has just started shows
 * Queued before the list has caught up with it (S4). The Status, Last import and Run time cells
 * all read it, so they never disagree about whether the row is moving (#2094).
 */
export function useConnectionRowActivity(dataflow: DataflowSummary): {
  showsLive: boolean;
  activity: RunActivity;
} {
  useStartedRunsVersion();
  const startedId = startedRuns.runFor(dataflow.id);
  const listed = dataflow.lastRun;
  const liveId = newestRunId(startedId, listed?.runId);
  const listedActive =
    listed !== undefined &&
    listed.runId === liveId &&
    (listed.status === "queued" || listed.status === "running") &&
    listed.stepsTotal !== undefined;
  const startedAhead = liveId !== null && liveId === startedId && liveId !== listed?.runId;
  const showsLive = liveId !== null && (listedActive || startedAhead);
  const trackedId = showsLive ? liveId : null;
  const activity = useRunActivity({
    runId: trackedId,
    dataflowId: dataflow.id,
    seed: trackedId !== null && listed?.runId === trackedId ? listed : null,
    fetchDetail: false,
  });
  return { showsLive, activity };
}

/**
 * The connections list's Status cell (owner feedback, 2026-09-28): the connection's one status,
 * and while an import is queued or running, its step as a second, smaller line ("Step 2 of 3 ·
 * Loading · 7 of 40 tables"). Every other row is one line. It never announces: the list's one
 * live line outside the grid speaks for the row started from this tab (SF13, lead fix round S1).
 */
export function ConnectionStatusCell({ dataflow }: { dataflow: DataflowSummary }) {
  const { showsLive, activity } = useConnectionRowActivity(dataflow);
  const need = useSetupNeed(dataflow.connectorName, dataflow.selectedEntityCount);
  const status = connectionStatusOf(
    dataflow,
    showsLive ? activity.phase : null,
    showsLive ? activity.runId : null,
  );
  // #1672: "Sending files to Snowflake" carries the time so far, which moves on its own.
  const now = useNow();
  const live = showsLive && isActivityLive(activity) ? activityLine(activity, "row", now) : null;
  // Before the run has steps the line is just "Running", the badge's own word (review r1 N4).
  // #787: a waiting row says why and what comes next, under the badge.
  const waiting = status === "waiting" ? connectionWaitingOf(dataflow) : null;
  const line =
    live === connectionStatusPresentation(status).label
      ? null
      : (live ?? (waiting ? waitingLine(waiting) : null));
  return (
    <StatusCell
      badge={
        <ConnectionStatusBadge
          status={status}
          need={need}
          testId={`state-${dataflow.displayName}`}
        />
      }
      secondary={
        line ? (
          <SkyText size={200} color="muted" testId={`status-line-${dataflow.displayName}`}>
            {line}
          </SkyText>
        ) : undefined
      }
    />
  );
}

/**
 * "Finished 2 hours ago", or "Started 5 minutes ago" for a run still going, whose finish is
 * null. Null when the run has neither time (queued, not started).
 */
function lastRunWhen(
  run: DataflowLastRun,
  zone: string,
  now: Date,
): { text: string; iso: string } | null {
  if (run.finishedAt) {
    const when = endedWhen(run.finishedAt, zone, now);
    return when ? { text: `Finished ${when}`, iso: run.finishedAt } : null;
  }
  if (run.startedAt) {
    const when = endedWhen(run.startedAt, zone, now);
    return when ? { text: `Started ${when}`, iso: run.startedAt } : null;
  }
  return null;
}

/**
 * The connections list's Last import cell.
 *
 * With `lastRun` (S8: `GET /dataflows` projects the run `lastRunId` names), the cell shows THAT
 * run: this connection's own outcome in it (`lastRun.outcome`, post-release 607e63a; the run's
 * status when an older server sends none) and, beside it on the same line, when it finished, as
 * a link to the run. A run whose extract succeeded and whose load failed reads "Succeeded with
 * warning" on the run page and "Failed" here, the word the Status column says.
 * This connection's rows are the list's own Rows column (Session 10, P4). The whole run's
 * duration is left out on purpose: a run can cover several connections, and the list cannot say
 * which share of it was this one's.
 *
 * One line unless there is a second thing to say (owner feedback, 2026-09-28: "Succeeded shows the
 * top part and the bottom shows empty"). The two cases with a second line:
 * - while the import is still moving: when it started with View, and the progress bar; its step
 *   is the Status column's second line, so the row never says it twice;
 * - a finished run that did not succeed: "Last successful refresh", because `last_refresh_at` is
 *   held back after a failed or partial run (`runner/steps/finalize.ts`), and that is the time a
 *   reader triaging a failure wants next.
 *
 * Without `lastRun` (no run yet, the run is gone, or an older server), the cell falls back to
 * what the summary alone knows: the outcome flags and the last successful refresh, labelled as
 * such so it never reads as the failed run's time (owner feedback).
 */
export function LastRunCell({ dataflow }: { dataflow: DataflowSummary }) {
  const { showsLive, activity } = useConnectionRowActivity(dataflow);
  return showsLive ? (
    <LiveLastImport dataflow={dataflow} activity={activity} name={dataflow.displayName} />
  ) : (
    <LastRunOutcome dataflow={dataflow} />
  );
}

/**
 * The connections list's one polite live line (SF13; P12 lead fix round, S1). It sits outside
 * the grid, is in the page before any import starts, and speaks only for the row whose import
 * was started from this tab (the newest such start among `dataflows`), from Queued to the
 * outcome. Rows moving on a schedule or someone else's start update silently. The list renders
 * it only for a viewer with `runs.start`; a Viewer's list holds no live line.
 *
 * Session 11 (P1): a run marked quiet (one of an "Import selected" batch) is skipped, so the line
 * keeps speaking for a single-row Import now only; a multi-row start is announced by its notice.
 */
export function ConnectionsImportLiveLine({
  dataflows,
}: {
  dataflows: readonly DataflowSummary[];
}) {
  const started = useStartedRuns();
  let row: DataflowSummary | null = null;
  let startedId: string | null = null;
  for (const dataflow of dataflows) {
    const runId = started.get(dataflow.id);
    if (runId && !startedRuns.isQuiet(runId) && newestRunId(runId, startedId) === runId) {
      row = dataflow;
      startedId = runId;
    }
  }
  const listed = row?.lastRun;
  // Followed while this tab's start is still the row's newest import.
  const trackedId =
    startedId !== null && newestRunId(startedId, listed?.runId) === startedId ? startedId : null;
  const activity = useRunActivity({
    runId: trackedId,
    dataflowId: row?.id ?? "",
    seed: trackedId !== null && listed?.runId === trackedId ? listed : null,
    fetchDetail: false,
  });
  const name = row ? row.displayName : "";
  const announcement = useImportAnnouncement(activity, name, { onlyStartedHere: true });
  return <LiveLine text={announcement} testId="connections-import-live" />;
}

/** The progress bar's name: queued, the step it is on, or just the import. */
function activityBarLabel(name: string, activity: RunActivity): string {
  if (activity.phase === "queued") return `${name} import: queued`;
  if (activity.stepIndex !== null && activity.stepTotal !== null) {
    return `${name} import: step ${activity.stepIndex} of ${activity.stepTotal}`;
  }
  return `${name} import`;
}

/**
 * The Last import cell of a row the list follows live. While the import is queued or running it
 * is two lines: when it started (or that it waits to start) with View, and the progress bar. The
 * step is the Status column's line. Once the tab sees it end, before the list has caught up, it
 * is the finished cell's one line from what the tab saw. It never announces (SF13, S1).
 */
function LiveLastImport({
  dataflow,
  activity,
  name,
}: {
  dataflow: DataflowSummary;
  activity: RunActivity;
  name: string;
}) {
  const testId = `last-import-${dataflow.displayName}`;
  const runId = activity.runId;
  // #1078: every hover is the instant in the reader's display zone, named; never a raw ISO.
  const zone = useDisplayTz();
  const now = useNow();
  if (!isActivityLive(activity)) {
    const badge = badgeOf(activity.phase);
    // #1216: the day word follows the display zone's calendar, never rounded hours.
    const when = endedWhen(activity.finishedAt, zone, now);
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
        {badge ? (
          <SkyBadge appearance="tint" color={badge.color} testId={`${testId}-badge`}>
            {badge.label}
          </SkyBadge>
        ) : null}
        {runId ? (
          // The badge says how it ended, so the time stands alone, as in the listed cell (N2).
          <ViewLink runId={runId} connectionName={name} testId={`${testId}-view`}>
            {when ?? "View"}
          </ViewLink>
        ) : null}
      </div>
    );
  }
  // A stuck start more than a day back reads its calendar day too (#1216).
  const started = endedWhen(activity.startedAt, zone, now);
  const barLabel = activityBarLabel(name, activity);
  return (
    <div className="flex min-w-0 flex-col items-start gap-1" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <SkyText size={200} color="muted" title={instantHoverTitle(activity.startedAt, zone)}>
          {started ? `Started ${started}` : "Waiting to start"}
        </SkyText>
        {runId ? (
          <ViewLink runId={runId} connectionName={name} testId={`${testId}-view`}>
            View
          </ViewLink>
        ) : null}
      </div>
      <SkyProgressBar
        thickness="small"
        value={activityProgress(activity)}
        label={barLabel}
        className="w-40 max-w-full"
        testId={`${testId}-bar`}
      />
    </div>
  );
}

function LastRunOutcome({ dataflow }: { dataflow: DataflowSummary }) {
  const run = dataflow.lastRun;
  const zone = useDisplayTz();
  const now = useNow();
  // #1216: every day word on the row follows the display zone's calendar (`endedWhen`), as the
  // run line under the badge and the schedule Run history do.
  const refreshedWhen = endedWhen(dataflow.lastRefreshAt, zone, now);
  const refreshLine = refreshedWhen ? (
    <SkyText
      size={200}
      color="muted"
      title={instantHoverTitle(dataflow.lastRefreshAt, zone)}
      testId={`last-refresh-${dataflow.displayName}`}
    >
      Last successful refresh {refreshedWhen}
    </SkyText>
  ) : null;

  if (run) {
    // This connection's own outcome once the API sends it (post-release 607e63a), so the cell
    // and the Status column never disagree; the run's status otherwise (an older server). #460
    // (option 2): the badge, the time and the link all name the listed run, so a cancelled run
    // whose outcome the server read from an earlier settled run reads Cancelled here, and the
    // earlier run is its own line (`SettledImportLine`).
    const shown = listedRunOwnStatus(run);
    const settled = settledImportOf(run);
    const listed = runStatusPresentation(shown);
    // #787: a gate's refusal reads Waiting here too, the word the Status column says.
    const status =
      !settled && connectionWaitingOf(dataflow)
        ? { ...listed, label: "Waiting", color: "informative" as const }
        : listed;
    const when = lastRunWhen(run, zone, now);
    const showRefresh = !status.isActive && shown !== "succeeded" && refreshLine !== null;
    const settledLine = settled ? (
      <SettledImportLine run={run} testId={`last-settled-${dataflow.displayName}`} />
    ) : null;
    const outcome = (
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid={showRefresh || settled ? undefined : `last-run-${dataflow.displayName}`}
      >
        <SkyBadge appearance="tint" color={status.color}>
          {status.label}
        </SkyBadge>
        <Link
          href={absolutePath(`/runs/${run.runId}`)}
          className="underline-offset-2 hover:underline"
          aria-label={`${when?.text ?? "Last run"}, open the run`}
          data-testid={`last-run-link-${dataflow.displayName}`}
        >
          {/* A finished run's badge already says how it ended, so the line beside it is the
              time alone ("2 hours ago"); the link's name keeps "Finished ...". */}
          <SkyText size={200} color="accent" title={instantHoverTitle(when?.iso, zone)}>
            {run.finishedAt && when
              ? endedWhen(run.finishedAt, zone, now)
              : (when?.text ?? "Open the run")}
          </SkyText>
        </Link>
      </div>
    );
    if (!showRefresh && !settledLine) return outcome;
    return (
      <div
        className="flex flex-col items-start gap-1"
        data-testid={`last-run-${dataflow.displayName}`}
      >
        {outcome}
        {settledLine}
        {showRefresh ? refreshLine : null}
      </div>
    );
  }

  const outcome = lastRunOutcomeOf(dataflow);
  const badge = (
    <SkyBadge appearance="tint" color={OUTCOME_COLOR[outcome]}>
      {OUTCOME_LABEL[outcome]}
    </SkyBadge>
  );
  if (!refreshLine) return <div className="flex items-center">{badge}</div>;
  return (
    <div className="flex flex-col items-start gap-1">
      {badge}
      {refreshLine}
    </div>
  );
}
